import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PageTabBar from '../components/PageTabBar';
import StockAtelierView from '../features/stock/StockAtelierView';
import StockDetailPanel from '../features/stock/StockDetailPanel';
import { STOCK_TABS } from './_stockTabs';
import ModalPortal from '../components/ModalPortal';
import {
  Trash2,
  CheckCircle,
  Download,
  Upload,
  ArrowRightLeft,
  Warehouse,
  Store,
  XCircle,
} from 'lucide-react';
import ArticleLabelModal from '../components/articles/ArticleLabelModal';
import ArticlePromotionModal from '../components/ArticlePromotionModal';
import CSVImportModal from '../components/CSVImportModal';
import CSVExportModal from '../components/CSVExportModal';

const StockPage = () => {
  const { api, isAdmin } = useAuth();
  const { t, tError } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  // STEP 3.5 multi-magasin (2026-05-15) — filter chip ('' = all)
  const [magasinFilter, setMagasinFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [transferModal, setTransferModal] = useState(null);
  const [transferQty, setTransferQty] = useState(1);
  const [transferFromLocation, setTransferFromLocation] = useState('boutique');
  const [transferToMagasin, setTransferToMagasin] = useState('M1');
  const [transferToLocation, setTransferToLocation] = useState('depot');
  const [transferring, setTransferring] = useState(false);
  const [labelArticle, setLabelArticle] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [promoArticle, setPromoArticle] = useState(null);
  const [promosByArticle, setPromosByArticle] = useState({});
  // STEP 3 multi-magasin — bulk-transfer state.
  const [bulkTransferModal, setBulkTransferModal] = useState(null);
  const [bulkTransferTo, setBulkTransferTo] = useState('M2');
  const [bulkTransferLocation, setBulkTransferLocation] = useState('boutique');
  const [bulkTransferQtyMap, setBulkTransferQtyMap] = useState({});
  const [bulkTransferResult, setBulkTransferResult] = useState(null);
  const [magasinsList, setMagasinsList] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const stockNavigate = useNavigate();

  // Fallback magasins labels (used only when the API returns no magasin).
  const fallbackMagasins = useMemo(() => ([
    { code: 'M1', name: t('stock.storeN', { n: 1 }) },
    { code: 'M2', name: t('stock.storeN', { n: 2 }) },
  ]), [t]);

  // Localize the shared stock tab labels (STOCK_TABS keeps FR labels as fallback).
  const localizedStockTabs = useMemo(
    () => STOCK_TABS.map((tb) => ({ ...tb, label: tb.labelKey ? t(tb.labelKey) : tb.label })),
    [t],
  );

  useEffect(() => {
    fetchArticles();
    fetchStats();
  }, [searchQuery, filter]);

  useEffect(() => {
    api.get('/magasins?active_only=true')
      .then((r) => setMagasinsList(r.data?.magasins || []))
      .catch(() => {});
  }, [api]);

  // Phase P1+ — load active per-article promotions to display badges + buttons.
  const fetchPromosMap = async () => {
    try {
      const res = await api.get('/admin/article-promotions/active');
      const map = {};
      (res.data?.promotions || []).forEach((p) => { map[p.article_id] = p; });
      setPromosByArticle(map);
    } catch {
      // Silent — badges are non-blocking.
    }
  };

  useEffect(() => { fetchPromosMap(); }, []);

  const filteredArticles = useMemo(
    () => articles.filter((a) => !magasinFilter || (a.magasin_id || 'M1') === magasinFilter),
    [articles, magasinFilter],
  );

  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 1280) return;
    if (!filteredArticles.length) {
      setSelectedArticleId(null);
      return;
    }
    if (!selectedArticleId || !filteredArticles.some((a) => a.id === selectedArticleId)) {
      setSelectedArticleId(filteredArticles[0].id);
    }
  }, [filteredArticles, selectedArticleId]);

  const handleSelectArticle = (id) => {
    setSelectedArticleId(id);
    if (typeof window !== 'undefined' && window.innerWidth < 1280) {
      setMobileDetailOpen(true);
    }
  };

  const selectedArticle = filteredArticles.find((a) => a.id === selectedArticleId) || null;

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/stock/import/template', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_stock.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('stock.err.templateDownload'));
    }
  };

  const handleCsvImport = async (result) => {
    // Modal already shows its own result step. Just refresh and surface
    // a compact banner on the page for the next 10 seconds.
    if (result) {
      setImportResult({
        inserted: result.created || 0,
        updated: result.updated || 0,
        skipped: result.skipped || 0,
        merged: result.merged || 0,
        errors: result.errors || [],
        duplicate_handling: result.default_policy || 'update',
        total: result.total_processed || 0,
      });
    }
    fetchArticles();
    fetchStats();
  };

  const retryWithUpdate = async () => {
    setImportResult(null);
    setShowImportModal(true);
  };

  const fetchArticles = async () => {
    try {
      let params = [];
      if (searchQuery) params.push(`q=${encodeURIComponent(searchQuery)}`);
      if (filter === 'rupture') params.push('rupture_only=true');
      
      const queryString = params.length ? `?${params.join('&')}` : '';
      const response = await api.get(`/stock${queryString}`);
      
      let data = response.data;
      if (filter === 'faible') {
        data = data.filter(a => a.quantite > 0 && a.quantite <= 3);
      }
      
      setArticles(data);
    } catch (error) {
      console.error('Error fetching stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/stock/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!id) {
      alert(t('stock.err.missingId'));
      return;
    }
    if (!window.confirm(t('stock.deleteConfirm'))) return;
    try {
      await api.delete(`/stock/${id}`);
      alert(t('stock.deleteSuccess'));
      fetchArticles();
      fetchStats();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert(tError(error.response?.data?.detail) || t('stock.err.delete'));
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(t('stock.confirmDeleteMany', { count: selectedIds.length }))) return;
    try {
      await Promise.all(selectedIds.map(id => api.delete(`/stock/${id}`)));
      setSelectedIds([]);
      fetchArticles();
      fetchStats();
    } catch (error) {
      alert(t('stock.err.delete'));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const toggleSelectAll = () => {
    const visibleIds = filteredArticles.map((a) => a.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
    if (allVisibleSelected) {
      setSelectedIds(selectedIds.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds([...new Set([...selectedIds, ...visibleIds])]);
    }
  };

  const handleBulkVisibility = async (visible) => {
    const msg = visible
      ? t('stock.confirmBulkPublish', { count: selectedIds.length })
      : t('stock.confirmBulkUnpublish', { count: selectedIds.length });
    if (!window.confirm(msg)) return;
    try {
      await api.put('/stock/bulk-visibility', { ids: selectedIds, visible_boutique: visible });
      setSelectedIds([]);
      fetchArticles();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('stock.err.update'));
    }
  };

  const handleToggleOnline = async (article) => {
    const visible = !article.visible_boutique;
    const msg = visible
      ? t('stock.confirmPublishOne', { name: article.designation })
      : t('stock.confirmUnpublishOne', { name: article.designation });
    if (!window.confirm(msg)) return;
    try {
      await api.put('/stock/bulk-visibility', { ids: [article.id], visible_boutique: visible });
      fetchArticles();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('stock.err.update'));
    }
  };

  const handleTransfer = async () => {
    if (!transferModal) return;
    const fromMagasin = (transferModal.magasin_id || 'M1').toUpperCase();
    if (transferFromLocation === 'depot' && fromMagasin !== 'M1') {
      alert(t('stock.depotOnlyM1'));
      return;
    }
    if (transferToLocation === 'depot' && transferToMagasin !== 'M1') {
      alert(t('stock.depotOnlyM1'));
      return;
    }
    const sameTarget = fromMagasin === transferToMagasin && transferFromLocation === transferToLocation;
    if (sameTarget) {
      alert(t('stock.sameSourceDest'));
      return;
    }
    setTransferring(true);
    try {
      await api.post('/stock/transfer-magasin', {
        reference: transferModal.reference,
        from_magasin_id: fromMagasin,
        to_magasin_id: transferToMagasin,
        quantity: transferQty,
        from_location: transferFromLocation,
        to_location: transferToLocation,
      });
      setTransferModal(null);
      setTransferQty(1);
      setTransferFromLocation('boutique');
      setTransferToLocation('depot');
      fetchArticles();
      fetchStats();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('stock.err.transfer'));
    } finally {
      setTransferring(false);
    }
  };

  const openTransfer = (article) => {
    setTransferModal(article);
    setTransferQty(1);
    const sourceMagasin = (article.magasin_id || 'M1').toUpperCase();
    const hasDepot = (article.quantite_depot || 0) > 0;
    const hasBoutique = (article.quantite || 0) > 0;
    const defaultFrom = hasBoutique ? 'boutique' : (hasDepot ? 'depot' : 'boutique');
    const availableMagasins = (magasinsList.length ? magasinsList : fallbackMagasins).map(m => m.code);
    const otherMagasin = availableMagasins.find(code => code !== sourceMagasin) || sourceMagasin;
    setTransferFromLocation(defaultFrom);
    setTransferToMagasin(otherMagasin);
    setTransferToLocation(defaultFrom === 'boutique' ? 'depot' : 'boutique');
  };

  useEffect(() => {
    if (transferToMagasin !== 'M1' && transferToLocation === 'depot') {
      setTransferToLocation('boutique');
    }
  }, [transferToMagasin, transferToLocation]);

  useEffect(() => {
    if (bulkTransferTo !== 'M1' && bulkTransferLocation === 'depot') {
      setBulkTransferLocation('boutique');
    }
  }, [bulkTransferTo, bulkTransferLocation]);

  // STEP 3 multi-magasin — bulk transfer between magasins.
  const runBulkTransfer = async () => {
    if (bulkTransferLocation === 'depot' && bulkTransferTo !== 'M1') {
      alert(t('stock.depotOnlyM1'));
      return;
    }

    const items = articles
      .filter(a => selectedIds.includes(a.id))
      .map(a => {
        const requested = bulkTransferQtyMap[a.id];
        const stock = bulkTransferLocation === 'boutique' ? (a.quantite || 0) : (a.quantite_depot || 0);
        const qty = Math.max(0, Math.min(requested ?? Math.min(1, stock), stock));
        return { article_id: a.id, reference: a.reference, quantity: qty, source_magasin: a.magasin_id || 'M1' };
      })
      .filter(i => i.quantity > 0 && i.source_magasin !== bulkTransferTo);

    if (items.length === 0) {
      alert(t('stock.noLineToTransfer'));
      return;
    }

    // Group items by from_magasin (the bulk-transfer endpoint expects one
    // source magasin per call, so we issue one call per source).
    const grouped = items.reduce((acc, it) => {
      (acc[it.source_magasin] = acc[it.source_magasin] || []).push(it);
      return acc;
    }, {});

    if (bulkTransferLocation === 'depot' && Object.keys(grouped).some(fromMag => fromMag !== 'M1')) {
      alert(t('stock.depotOnlyM1'));
      return;
    }

    let aggregated = { succeeded: 0, failed: 0, transferred_total: 0, results: [] };
    try {
      for (const [fromMag, batch] of Object.entries(grouped)) {
        const idem = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `bulk-${Date.now()}-${fromMag}`;
        const res = await api.post('/stock/bulk-transfer', {
          from_magasin_id: fromMag,
          to_magasin_id: bulkTransferTo,
          location: bulkTransferLocation,
          items: batch.map(b => ({ article_id: b.article_id, reference: b.reference, quantity: b.quantity })),
        }, { headers: { 'Idempotency-Key': idem } });
        aggregated.succeeded += res.data?.succeeded || 0;
        aggregated.failed += res.data?.failed || 0;
        aggregated.transferred_total += res.data?.transferred_total || 0;
        aggregated.results = aggregated.results.concat(res.data?.results || []);
      }
      setBulkTransferResult(aggregated);
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('stock.err.bulkTransfer'));
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in" data-testid="stock-page">
        <PageTabBar tabs={localizedStockTabs} activeColor="rose" />

        {importResult && (
          <div
            className="rounded-xl border p-4 mb-3 space-y-2"
            style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
            data-testid="stock-import-result"
          >
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-[var(--rm-success)] mt-0.5 shrink-0" />
              <div className="flex-1 text-sm">
                <div className="text-[var(--rm-white)] font-semibold mb-1">{t('stock.importDone')}</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div className="rounded-lg px-2 py-1.5 border" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-success-muted)' }}>
                    <div className="text-[var(--rm-success)] font-bold text-base">{importResult.inserted}</div>
                    <div className="text-[var(--rm-gray-500)]">{t('stock.importCreated')}</div>
                  </div>
                  <div className="rounded-lg px-2 py-1.5 border" style={{ borderColor: 'var(--rm-border-subtle)' }}>
                    <div className="text-[var(--rm-white)] font-bold text-base">{importResult.updated}</div>
                    <div className="text-[var(--rm-gray-500)]">{t('stock.importUpdated')}</div>
                  </div>
                  <div className="rounded-lg px-2 py-1.5 border" style={{ borderColor: 'var(--rm-border-subtle)' }}>
                    <div className="text-[var(--rm-gray-300)] font-bold text-base">{importResult.skipped}</div>
                    <div className="text-[var(--rm-gray-500)]">{t('stock.importSkipped')}</div>
                  </div>
                  <div className="rounded-lg px-2 py-1.5 border" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-red-muted)' }}>
                    <div className="text-[var(--rm-red-light)] font-bold text-base">{(importResult.errors || []).length}</div>
                    <div className="text-[var(--rm-gray-500)]">{t('stock.importErrors')}</div>
                  </div>
                </div>
                {importResult.skipped > 0 && importResult.duplicate_handling === 'skip' && (
                  <div className="mt-3 rounded-lg p-2 flex flex-wrap items-center justify-between gap-2 border" style={{ borderColor: 'var(--rm-border-subtle)' }} data-testid="stock-import-retry-update">
                    <span className="text-[var(--rm-gray-400)] text-xs">
                      {t('stock.importRetryHint', { count: importResult.skipped })}
                    </span>
                    <button
                      type="button"
                      onClick={retryWithUpdate}
                      className="px-3 py-2 min-h-[44px] rounded-xl border text-xs font-semibold hover:bg-white/5"
                      style={{ borderColor: 'var(--rm-border-subtle)', color: 'var(--rm-gray-200)' }}
                      data-testid="stock-import-retry-btn"
                    >
                      {t('stock.importRetryBtn')}
                    </button>
                  </div>
                )}
                {(importResult.errors || []).length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[var(--rm-red-light)] cursor-pointer text-xs">{t('stock.importViewErrors', { count: importResult.errors.length })}</summary>
                    <ul className="mt-1 text-[var(--rm-gray-400)] text-[11px] list-disc pl-5 max-h-32 overflow-y-auto">
                      {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </details>
                )}
              </div>
              <button type="button" onClick={() => setImportResult(null)} className="text-[var(--rm-gray-500)] hover:text-white">✕</button>
            </div>
          </div>
        )}

        <StockAtelierView
          stats={stats}
          loading={loading}
          articles={filteredArticles}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filter={filter}
          onFilterChange={setFilter}
          magasinFilter={magasinFilter}
          onMagasinFilterChange={setMagasinFilter}
          magasinsList={magasinsList}
          filtersOpen={filtersOpen}
          onToggleFiltersOpen={() => setFiltersOpen((v) => !v)}
          selectedId={selectedArticleId}
          onSelectId={handleSelectArticle}
          promosByArticle={promosByArticle}
          isAdmin={isAdmin}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          onImport={() => setShowImportModal(true)}
          onExport={() => setShowExportModal(true)}
          onInvoiceImport={() => stockNavigate('/stock/import-facture')}
          onTransfer={openTransfer}
          onPromo={setPromoArticle}
          onLabel={(article) => setLabelArticle({ id: article.id, ref: article.reference })}
          onDelete={handleDelete}
          onToggleOnline={handleToggleOnline}
          onBulkOnline={() => handleBulkVisibility(true)}
          onBulkOffline={() => handleBulkVisibility(false)}
          onBulkTransfer={() => setBulkTransferModal({ open: true })}
          onBulkDelete={handleDeleteSelected}
          t={t}
        />

        <ModalPortal isOpen={mobileDetailOpen && !!selectedArticle}>
          <div className="modal-dark w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="modal-dark-header flex items-center justify-between sticky top-0 bg-[#151820] z-10">
              <h3 className="font-bold font-mono text-red-400">{selectedArticle?.reference}</h3>
              <button type="button" onClick={() => setMobileDetailOpen(false)} className="text-gray-400 hover:text-white text-xl leading-none">×</button>
            </div>
            <div className="p-4">
              <StockDetailPanel
                article={selectedArticle}
                promo={selectedArticle ? promosByArticle[selectedArticle.id] : null}
                isAdmin={isAdmin}
                onTransfer={openTransfer}
                onPromo={setPromoArticle}
                onLabel={(article) => setLabelArticle({ id: article.id, ref: article.reference })}
                onDelete={handleDelete}
                onToggleOnline={handleToggleOnline}
              />
            </div>
          </div>
        </ModalPortal>

        <CSVImportModal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          onConfirm={handleCsvImport}
        />

        <CSVExportModal
          open={showExportModal}
          onClose={() => setShowExportModal(false)}
          api={api}
        />

        <ArticlePromotionModal
          open={!!promoArticle}
          article={promoArticle}
          onClose={() => setPromoArticle(null)}
          onCreated={() => { fetchPromosMap(); fetchArticles(); }}
        />

        {/* Transfer Modal */}
        {transferModal && (
          <ModalPortal isOpen={true}>
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setTransferModal(null)}>
              <div className="modal-dark w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()} data-testid="stock-transfer-modal">
                <div className="modal-dark-header">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <ArrowRightLeft className="w-5 h-5 text-[var(--rm-gray-400)]" />
                    {t('stock.transferTitle')}
                  </h3>
                </div>
                <div className="p-6">
                  <p className="text-sm text-[var(--rm-gray-400)] mb-4">{transferModal.designation}</p>

                  <div className="grid grid-cols-2 gap-3 mb-4 text-center">
                    <div className="rounded-lg border p-3" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}>
                      <Store className="w-5 h-5 text-[var(--rm-success)] mx-auto mb-1" />
                      <div className="text-xl font-bold text-[var(--rm-success)]">{transferModal.quantite}</div>
                      <div className="text-xs text-[var(--rm-gray-500)]">{t('stock.boutique')}</div>
                    </div>
                    <div className="rounded-lg border p-3" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}>
                      <Warehouse className="w-5 h-5 text-[var(--rm-gray-400)] mx-auto mb-1" />
                      <div className="text-xl font-bold text-[var(--rm-gray-200)]">{transferModal.quantite_depot || 0}</div>
                      <div className="text-xs text-[var(--rm-gray-500)]">{t('stock.depot')}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-sm text-[var(--rm-gray-300)] mb-2">{t('stock.sourceStore')}</label>
                      <input
                        value={(transferModal.magasin_id || 'M1').toUpperCase()}
                        readOnly
                        className="input-dark w-full py-3 min-h-[44px] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[var(--rm-gray-300)] mb-2">{t('stock.sourceLocation')}</label>
                      <select
                        data-testid="transfer-source-location"
                        value={transferFromLocation}
                        onChange={(e) => setTransferFromLocation(e.target.value)}
                        className="input-dark w-full py-3 min-h-[44px] text-sm"
                      >
                        <option value="boutique">{t('stock.boutique')}</option>
                        {(transferModal.magasin_id || 'M1').toUpperCase() === 'M1' && (
                          <option value="depot">{t('stock.depot')}</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-[var(--rm-gray-300)] mb-2">{t('stock.destStore')}</label>
                      <select
                        data-testid="transfer-destination-magasin"
                        value={transferToMagasin}
                        onChange={(e) => setTransferToMagasin(e.target.value)}
                        className="input-dark w-full py-3 min-h-[44px] text-sm"
                      >
                        {(magasinsList.length ? magasinsList : fallbackMagasins).map((m) => (
                          <option key={m.code} value={m.code}>{m.name} ({m.code})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-[var(--rm-gray-300)] mb-2">{t('stock.destLocation')}</label>
                      <select
                        data-testid="transfer-destination-location"
                        value={transferToLocation}
                        onChange={(e) => setTransferToLocation(e.target.value)}
                        className="input-dark w-full py-3 min-h-[44px] text-sm"
                      >
                        <option value="boutique">{t('stock.boutique')}</option>
                        {transferToMagasin === 'M1' && (
                          <option value="depot">{t('stock.depot')}</option>
                        )}
                      </select>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm text-[var(--rm-gray-300)] mb-2">{t('stock.quantity')}</label>
                    <input
                      data-testid="transfer-qty"
                      type="number"
                      min="1"
                      max={transferFromLocation === 'boutique' ? (transferModal.quantite || 0) : (transferModal.quantite_depot || 0)}
                      value={transferQty}
                      onChange={(e) => setTransferQty(parseInt(e.target.value, 10) || 1)}
                      className="input-dark w-full py-3 min-h-[44px] text-sm"
                    />
                    <p className="text-xs text-[var(--rm-gray-500)] mt-2">
                      {t('stock.availableAtSource', { count: transferFromLocation === 'boutique' ? (transferModal.quantite || 0) : (transferModal.quantite_depot || 0) })}
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setTransferModal(null)}
                      className="flex-1 py-3 min-h-[44px] rounded-xl border font-medium text-sm text-[var(--rm-gray-300)] hover:bg-white/5"
                      style={{ borderColor: 'var(--rm-border-subtle)' }}
                    >
                      {t('stock.cancel')}
                    </button>
                    <button
                      type="button"
                      data-testid="confirm-transfer-btn"
                      onClick={handleTransfer}
                      disabled={transferring}
                      className="flex-1 py-3 min-h-[44px] rounded-xl btn-accent font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <ArrowRightLeft className="w-4 h-4" />
                      {transferring ? t('stock.transferring') : t('stock.transfer')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </ModalPortal>
        )}

        {/* Feature 2 — Article label modal */}
        <ArticleLabelModal
          open={!!labelArticle}
          articleId={labelArticle?.id}
          articleReference={labelArticle?.ref}
          onClose={() => setLabelArticle(null)}
        />

        {/* STEP 3 multi-magasin — Bulk transfer modal */}
        {bulkTransferModal?.open && (
          <ModalPortal>
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9999}}>
              <div className="modal-dark w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="modal-dark-header flex items-center justify-between">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <ArrowRightLeft className="w-5 h-5 text-[var(--rm-gray-400)]" />
                    {t('stock.bulkTransferTitle', { count: selectedIds.length })}
                  </h3>
                  <button onClick={() => { setBulkTransferModal(null); setBulkTransferResult(null); }} className="text-gray-400 hover:text-white" data-testid="bulk-transfer-close">
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-5 space-y-4 text-sm">
                  {!bulkTransferResult && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-400 mb-1 block">{t('stock.destStore')}</label>
                          <select
                            value={bulkTransferTo}
                            onChange={(e) => setBulkTransferTo(e.target.value)}
                            className="input-dark w-full py-2 px-3"
                            data-testid="bulk-transfer-dest"
                          >
                            {(magasinsList.length ? magasinsList : fallbackMagasins).map(m => (
                              <option key={m.code} value={m.code}>{m.name} ({m.code})</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 mb-1 block">{t('stock.location')}</label>
                          <select
                            value={bulkTransferLocation}
                            onChange={(e) => setBulkTransferLocation(e.target.value)}
                            className="input-dark w-full py-2 px-3"
                            data-testid="bulk-transfer-location"
                          >
                            <option value="boutique">{t('stock.boutique')}</option>
                            {bulkTransferTo === 'M1' && <option value="depot">{t('stock.depot')}</option>}
                          </select>
                        </div>
                      </div>
                      <div className="rounded-lg border p-3 text-xs text-[var(--rm-gray-400)]" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}>
                        {t('stock.bulkTransferR1Note')}
                      </div>
                      <div className="overflow-x-auto max-h-72 border border-white/10 rounded-lg">
                        <table className="w-full text-xs">
                          <thead className="bg-white/5 text-gray-400">
                            <tr>
                              <th className="p-2 text-left">{t('stock.colArticle')}</th>
                              <th className="p-2 text-left">{t('stock.colCurrentStore')}</th>
                              <th className="p-2 text-right">{t('stock.colStock')}</th>
                              <th className="p-2 text-right">{t('stock.colQtyToTransfer')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {articles.filter(a => selectedIds.includes(a.id)).map(a => {
                              const stock = bulkTransferLocation === 'boutique' ? (a.quantite || 0) : (a.quantite_depot || 0);
                              const qty = bulkTransferQtyMap[a.id] ?? Math.min(1, stock);
                              return (
                                <tr key={a.id} className="border-t border-white/5">
                                  <td className="p-2">
                                    <div className="text-white font-semibold">{a.designation}</div>
                                    <div className="text-gray-500 font-mono text-[10px]">{a.reference}</div>
                                  </td>
                                  <td className="p-2 text-[var(--rm-gray-300)]">{a.magasin_id || 'M1'}</td>
                                  <td className="p-2 text-right text-gray-300">{stock}</td>
                                  <td className="p-2 text-right">
                                    <input
                                      type="number"
                                      min="0"
                                      max={stock}
                                      value={qty}
                                      onChange={(e) => setBulkTransferQtyMap({...bulkTransferQtyMap, [a.id]: parseInt(e.target.value, 10) || 0})}
                                      className="input-dark w-20 py-1 px-2 text-right text-xs"
                                      data-testid={`bulk-transfer-qty-${a.id}`}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <button onClick={() => setBulkTransferModal(null)} className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm">{t('stock.cancel')}</button>
                        <button
                          onClick={runBulkTransfer}
                          className="px-4 py-2 rounded-lg btn-accent font-semibold text-sm"
                          data-testid="bulk-transfer-confirm"
                        >
                          {t('stock.runTransfer')}
                        </button>
                      </div>
                    </>
                  )}

                  {bulkTransferResult && (
                    <>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-lg border p-3" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-success-muted)' }}>
                          <div className="text-2xl font-bold text-[var(--rm-success)]">{bulkTransferResult.succeeded || 0}</div>
                          <div className="text-xs text-[var(--rm-gray-500)]">{t('stock.resultSucceeded')}</div>
                        </div>
                        <div className="rounded-lg border p-3" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-red-muted)' }}>
                          <div className="text-2xl font-bold text-[var(--rm-red-light)]">{bulkTransferResult.failed || 0}</div>
                          <div className="text-xs text-[var(--rm-gray-500)]">{t('stock.resultFailed')}</div>
                        </div>
                        <div className="rounded-lg border p-3" style={{ borderColor: 'var(--rm-border-subtle)' }}>
                          <div className="text-2xl font-bold text-[var(--rm-white)]">{bulkTransferResult.transferred_total || 0}</div>
                          <div className="text-xs text-[var(--rm-gray-500)]">{t('stock.resultUnitsTransferred')}</div>
                        </div>
                      </div>
                      {(bulkTransferResult.results || []).filter((r) => !r.ok).length > 0 && (
                        <div className="text-xs rounded-lg p-3 max-h-40 overflow-y-auto border" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-red-muted)' }}>
                          <strong className="text-[var(--rm-red-light)]">{t('stock.failuresLabel')}</strong>
                          <ul className="list-disc pl-4 mt-1 text-[var(--rm-gray-300)]">
                            {(bulkTransferResult.results || []).filter(r => !r.ok).map((r, i) => (
                              <li key={i}>{r.reference || r.article_id || '?'} : {r.reason}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="flex justify-end">
                        <button
                          onClick={() => { setBulkTransferModal(null); setBulkTransferResult(null); setBulkTransferQtyMap({}); setSelectedIds([]); fetchArticles(); fetchStats(); }}
                          className="px-4 py-2 rounded-lg btn-accent font-semibold text-sm"
                          data-testid="bulk-transfer-done"
                        >
                          {t('stock.done')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </ModalPortal>
        )}
      </div>
    </Layout>
  );
};

export default StockPage;
